import { useEffect, useRef } from "react";

/**
 * CursorGlow — places a soft blue radial blob inside the nearest positioned
 * ancestor. The blob smoothly follows the user's cursor while inside the
 * section. Falls back to a centered position on leave / on mobile.
 *
 * The parent must be `position: relative` (or absolute/fixed). Place inside
 * any section that uses the soft-blue background fade.
 */
export default function CursorGlow({ size = 600 }) {
  const blobRef = useRef(null);

  useEffect(() => {
    const blob = blobRef.current;
    const parent = blob?.parentElement;
    if (!parent) return;

    const setCenter = () => {
      const rect = parent.getBoundingClientRect();
      const x = rect.width / 2 - size / 2;
      const y = rect.height / 2 - size / 2;
      blob.style.transform = `translate3d(${x}px, ${y}px, 0)`;
    };

    const handleMove = (e) => {
      const rect = parent.getBoundingClientRect();
      const x = e.clientX - rect.left - size / 2;
      const y = e.clientY - rect.top - size / 2;
      blob.style.transform = `translate3d(${x}px, ${y}px, 0)`;
      blob.style.opacity = "1";
    };

    const handleLeave = () => {
      blob.style.opacity = "0.55";
      setCenter();
    };

    setCenter();
    parent.addEventListener("mousemove", handleMove);
    parent.addEventListener("mouseleave", handleLeave);
    window.addEventListener("resize", setCenter);

    return () => {
      parent.removeEventListener("mousemove", handleMove);
      parent.removeEventListener("mouseleave", handleLeave);
      window.removeEventListener("resize", setCenter);
    };
  }, [size]);

  return (
    <div
      ref={blobRef}
      aria-hidden
      data-testid="cursor-glow"
      className="cursor-glow-blob"
      style={{
        width: size,
        height: size,
        opacity: 0.55,
      }}
    />
  );
}
